import type { AreaSubstrate, QuoteProduct } from 'paintscout'
import get from 'lodash/get'
import type { UpdateableAreaSubstrate, UpdateableProduct } from '../../..'
import { parseNumber } from '../../../../../../calculator'

/**
 * convert product details on UpdateableAreaSubstrate to AreaSubstrate to be saved
 */
export default function toSaveableProducts(
  baseAreaSubstrate: AreaSubstrate,
  areaSubstrate: UpdateableAreaSubstrate
): QuoteProduct[] {
  if (!areaSubstrate || !areaSubstrate?.products?.length) return []

  const defaultProducts = getDefaultProducts(baseAreaSubstrate, areaSubstrate)

  return defaultProducts
}

function getDefaultProducts(baseAreaSubstrate: AreaSubstrate, areaSubstrate: UpdateableAreaSubstrate): QuoteProduct[] {
  if (!areaSubstrate.products) return []
  return areaSubstrate.products
    .map((product) => {
      const convertedProduct = convertProduct(baseAreaSubstrate, product)
      return convertedProduct
    })
    .filter(Boolean)
}

function convertProduct(baseAreaSubstrate: AreaSubstrate, product: UpdateableProduct): QuoteProduct {
  if (!product || !product.key) return null
  const baseProduct = get(baseAreaSubstrate, 'product.product', {})

  const totalPrice = product.totalPrice
    ? {
        totalPrice: parseNumber(product?.totalPrice?.default, 0),
        totalPrice_override: product.totalPrice.useCustom,
        totalPrice_override_value: parseNumber(product?.totalPrice?.custom, 0)
      }
    : {}

  const price = product.price
    ? {
        price: parseNumber(product?.price?.default, 0),
        price_override: product.price.useCustom,
        price_override_value: parseNumber(product?.price?.custom, 0)
      }
    : {}

  // tier swap state belongs to each slot, so never inherit it from the legacy product spread below
  const {
    tierBaseProductKey: _legacyTierBaseProductKey,
    tierBaseCoverage: _legacyTierBaseCoverage,
    ...legacyProduct
  } = baseAreaSubstrate.product ?? ({} as QuoteProduct)

  return {
    ...legacyProduct,
    product: {
      ...baseProduct,
      label: product.label,
      name: product.key,
      unitLabel: product.unitLabel,
      color: product.color,
      manufacturer: product.manufacturer
    },

    calculate: product.calculate,

    coverage_rate_value: parseNumber(product?.coverage?.default, 0),
    coverage_rate_override: product?.coverage?.useCustom,
    coverage_rate_override_value: parseNumber(product?.coverage?.custom, 0),

    lnft_coverage_rate_value: parseNumber(product?.lnftCoverage?.default, 0),
    lnft_coverage_rate_override: product?.lnftCoverage?.useCustom,
    lnft_coverage_rate_override_value: parseNumber(product?.lnftCoverage?.custom, 0),

    quantity_coverage_rate_value: parseNumber(product?.quantityCoverage?.default, 0),
    quantity_coverage_rate_override: product?.quantityCoverage?.useCustom,
    quantity_coverage_rate_override_value: parseNumber(product?.quantityCoverage?.custom, 0),

    volume: parseNumber(product?.volume?.default, 0),
    volume_override: product?.volume?.useCustom,
    volume_override_value: parseNumber(product?.volume?.custom, 0),

    ...price,
    ...totalPrice,

    coats: product.coats,

    quoteTypes: product.quoteTypes,
    custom: product.custom,

    // tier swap state — dropping it leaves a swapped product on every tier (ENG-2211)
    ...(product.tierBaseProductKey ? { tierBaseProductKey: product.tierBaseProductKey } : {}),
    ...(product.tierBaseCoverage ? { tierBaseCoverage: product.tierBaseCoverage } : {})
  }
}
